/**
 * Message Validation Schemas
 *
 * Simple validation functions for message types.
 * Returns true if valid, false otherwise.
 *
 * Note: These are lightweight runtime validators. For production,
 * consider using a library like Zod or Yup for more robust validation.
 */

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isVector3 = (value) =>
  Array.isArray(value) && value.length === 3 && value.every((n) => typeof n === 'number');

/**
 * Validate a standard `robot.status` message value:
 *   {
 *     robot_info:    { robot_id, sub_id, angle_unit, robot_type: { category, model }, capability },
 *     robot_state:   { base_state, pose, velocity: { linear, angular, frame },
 *                      battery_state: { percentage, is_charging, ... }, joint_state, raw_status, extension },
 *     mission_info?: { mission_state, mission_name, current_task, available_missions }
 *   }
 * The flat `robot_status` format ({ robot_id, battery_level, charging, ... }) is deprecated and rejected.
 * @param {Object} message - Message value to validate
 * @returns {boolean} True if valid
 */
export const RobotStatusSchema = (message) => {
  if (!isPlainObject(message)) {
    return false;
  }

  const { robot_info: robotInfo, robot_state: robotState, mission_info: missionInfo } = message;

  // Required sections
  if (!isPlainObject(robotInfo) || !robotInfo.robot_id) {
    console.warn('[RobotStatusSchema] Missing robot_info.robot_id');
    return false;
  }
  if (!isPlainObject(robotState)) {
    console.warn('[RobotStatusSchema] Missing robot_state');
    return false;
  }

  // Optional but typed fields of robot_state
  if (robotState.base_state != null && typeof robotState.base_state !== 'string') {
    console.warn('[RobotStatusSchema] Invalid robot_state.base_state');
    return false;
  }
  if (robotState.pose !== undefined && !isPlainObject(robotState.pose)) {
    console.warn('[RobotStatusSchema] Invalid robot_state.pose');
    return false;
  }
  if (robotState.velocity !== undefined) {
    const { linear, angular } = robotState.velocity ?? {};
    if (
      !isPlainObject(robotState.velocity) ||
      (linear !== undefined && !isVector3(linear)) ||
      (angular !== undefined && !isVector3(angular))
    ) {
      console.warn('[RobotStatusSchema] Invalid robot_state.velocity');
      return false;
    }
  }
  if (robotState.battery_state !== undefined) {
    const battery = robotState.battery_state;
    if (!isPlainObject(battery)) {
      console.warn('[RobotStatusSchema] Invalid robot_state.battery_state');
      return false;
    }
    if (
      battery.percentage !== undefined &&
      (typeof battery.percentage !== 'number' || battery.percentage < 0 || battery.percentage > 100)
    ) {
      console.warn('[RobotStatusSchema] Invalid robot_state.battery_state.percentage');
      return false;
    }
    if (battery.is_charging !== undefined && typeof battery.is_charging !== 'boolean') {
      console.warn('[RobotStatusSchema] Invalid robot_state.battery_state.is_charging');
      return false;
    }
  }

  // Optional mission section
  if (missionInfo != null) {
    if (!isPlainObject(missionInfo)) {
      console.warn('[RobotStatusSchema] Invalid mission_info');
      return false;
    }
    if (missionInfo.available_missions !== undefined && !Array.isArray(missionInfo.available_missions)) {
      console.warn('[RobotStatusSchema] Invalid mission_info.available_missions');
      return false;
    }
  }

  return true;
};

/**
 * Validate command velocity message
 * @param {Object} message - Message to validate
 * @returns {boolean} True if valid
 */
export const CmdVelSchema = (message) => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  // Should have linear and/or angular velocity
  if (!message.linear && !message.angular) {
    console.warn('[CmdVelSchema] Missing velocity fields');
    return false;
  }

  // Validate linear velocity
  if (message.linear !== undefined) {
    if (typeof message.linear !== 'object') {
      return false;
    }
    if (message.linear.x !== undefined && typeof message.linear.x !== 'number') {
      return false;
    }
    if (message.linear.y !== undefined && typeof message.linear.y !== 'number') {
      return false;
    }
    if (message.linear.z !== undefined && typeof message.linear.z !== 'number') {
      return false;
    }
  }

  // Validate angular velocity
  if (message.angular !== undefined) {
    if (typeof message.angular !== 'object') {
      return false;
    }
    if (message.angular.x !== undefined && typeof message.angular.x !== 'number') {
      return false;
    }
    if (message.angular.y !== undefined && typeof message.angular.y !== 'number') {
      return false;
    }
    if (message.angular.z !== undefined && typeof message.angular.z !== 'number') {
      return false;
    }
  }

  return true;
};

/**
 * Validate joystick message
 * @param {Object} message - Message to validate
 * @returns {boolean} True if valid
 */
export const JoySchema = (message) => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  // Should have axes and/or buttons
  if (!message.axes && !message.buttons) {
    console.warn('[JoySchema] Missing axes or buttons');
    return false;
  }

  // Validate axes
  if (message.axes !== undefined) {
    if (!Array.isArray(message.axes)) {
      return false;
    }
    if (!message.axes.every(axis => typeof axis === 'number')) {
      return false;
    }
  }

  // Validate buttons
  if (message.buttons !== undefined) {
    if (!Array.isArray(message.buttons)) {
      return false;
    }
    if (!message.buttons.every(button => typeof button === 'number')) {
      return false;
    }
  }

  return true;
};

/**
 * Validate mission status message
 * @param {Object} message - Message to validate
 * @returns {boolean} True if valid
 */
export const MissionStatusSchema = (message) => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  // Required fields
  if (!message.mission_id) {
    console.warn('[MissionStatusSchema] Missing mission_id');
    return false;
  }

  if (!message.status) {
    console.warn('[MissionStatusSchema] Missing status');
    return false;
  }

  // Validate status is a known value
  const validStatuses = ['pending', 'running', 'paused', 'completed', 'failed', 'cancelled'];
  if (!validStatuses.includes(message.status)) {
    console.warn('[MissionStatusSchema] Invalid status value:', message.status);
    return false;
  }

  return true;
};

/**
 * Validate data channel message wrapper
 * @param {Object} message - Message to validate
 * @returns {boolean} True if valid
 */
export const DataChannelMessageSchema = (message) => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  // Should have channel and data fields
  if (!message.channel) {
    console.warn('[DataChannelMessageSchema] Missing channel');
    return false;
  }

  if (message.data === undefined) {
    console.warn('[DataChannelMessageSchema] Missing data');
    return false;
  }

  // Validate channel name
  if (typeof message.channel !== 'string' || message.channel.length === 0) {
    console.warn('[DataChannelMessageSchema] Invalid channel name');
    return false;
  }

  return true;
};

/**
 * Validate generic message with topic and value
 * @param {Object} message - Message to validate
 * @returns {boolean} True if valid
 */
export const TopicMessageSchema = (message) => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  if (!message.topic || typeof message.topic !== 'string') {
    console.warn('[TopicMessageSchema] Invalid or missing topic');
    return false;
  }

  if (message.value === undefined) {
    console.warn('[TopicMessageSchema] Missing value');
    return false;
  }

  return true;
};

/**
 * Create a composite schema that validates against multiple schemas
 * @param {...Function} schemas - Schema functions to combine
 * @returns {Function} Combined schema function
 */
export const combineSchemas = (...schemas) => {
  return (message) => {
    return schemas.every(schema => schema(message));
  };
};

/**
 * Create an optional schema (always valid if message is null/undefined)
 * @param {Function} schema - Schema function
 * @returns {Function} Optional schema function
 */
export const optional = (schema) => {
  return (message) => {
    if (message === null || message === undefined) {
      return true;
    }
    return schema(message);
  };
};

/**
 * Create a schema that validates array of messages
 * @param {Function} schema - Schema for individual items
 * @returns {Function} Array schema function
 */
export const arrayOf = (schema) => {
  return (messages) => {
    if (!Array.isArray(messages)) {
      return false;
    }
    return messages.every(msg => schema(msg));
  };
};

export default {
  RobotStatusSchema,
  CmdVelSchema,
  JoySchema,
  MissionStatusSchema,
  DataChannelMessageSchema,
  TopicMessageSchema,
  combineSchemas,
  optional,
  arrayOf,
};
